import React, { useState } from 'react';
import { Lock, Mail, Loader2, AlertTriangle, KeyRound, X, ArrowRight } from 'lucide-react';
import api from '../services/api'; // Importa a configuração centralizada do Axios

const LoginScreen = () => {
    // --- ESTADOS DE LOGIN ---
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // --- ESTADOS DE RECUPERAÇÃO DE SENHA ---
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState('');

    // --- 1. FUNÇÃO DE LOGIN ---
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Chama o servidor usando a instância configurada no api.js (Porta 3015)
            const response = await api.post('/login', {
                email,
                password
            });

            const { token, user } = response.data;

            // Salva sessão localmente
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Força recarregamento para entrar no MainLayout
            window.location.reload();

        } catch (err) {
            console.error("Erro no login:", err);
            if (err.response) {
                // Erro retornado pela API (ex: 401 - Senha incorreta)
                setError(err.response.data.error || 'Credenciais inválidas.');
            } else if (err.request) {
                // Erro de conexão (Servidor desligado ou porta errada)
                setError('Não foi possível conectar ao servidor local (3015). Verifique se ele está rodando.');
            } else {
                setError('Erro desconhecido. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    // --- 2. FUNÇÃO DE RESETAR SENHA ---
    const handleResetPassword = async (e) => {
        e.preventDefault();
        setResetLoading(true);
        setResetSuccess('');
        setError(''); 

        try {
            // Chama rota de reset usando a instância api
            await api.post('/reset-password', { email: resetEmail });
            
            setResetSuccess(`Um link de redefinição (ou nova senha) foi enviado para ${resetEmail}`);
            setTimeout(() => {
                setShowResetModal(false);
                setResetSuccess('');
                setResetEmail('');
            }, 3000);

        } catch (err) {
            // Mesmo em caso de erro, por segurança, às vezes não avisamos se o email existe ou não, 
            // mas aqui deixaremos o alerta para facilitar o debug.
            alert('Erro ao solicitar redefinição. Verifique a conexão ou se o e-mail é válido.');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans">
            <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md relative overflow-hidden">
                
                {/* Cabeçalho */}
                <div className="text-center mb-8 relative z-10">
                    <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform">
                        <Lock className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800">WA-Desk Local</h1>
                </div>

                {/* Mensagem de Erro Geral */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex items-start text-sm animate-in fade-in slide-in-from-top-2">
                        <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Formulário de Login */}
                <form onSubmit={handleLogin} className="space-y-5 relative z-10">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">E-mail Corporativo</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                            </div>
                            <input 
                                type="email" 
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all sm:text-sm"
                                placeholder="seu@email.com"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">Senha de Acesso</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                            </div>
                            <input 
                                type="password" 
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all sm:text-sm"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                        <div className="text-right mt-1">
                            <button 
                                type="button"
                                onClick={() => setShowResetModal(true)}
                                className="text-xs font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline"
                            >
                                Esqueceu a senha?
                            </button>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform active:scale-95"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                Autenticando...
                            </>
                        ) : (
                            <>
                                Entrar no Sistema <ArrowRight className="ml-2 w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>

                {/* Rodapé */}
                <div className="mt-8 text-center text-xs text-gray-400">
                    &copy; {new Date().getFullYear()} WA-Desk Self-Hosted.
                </div>
            </div>

            {/* --- MODAL DE REDEFINIÇÃO DE SENHA --- */}
            {showResetModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-800 flex items-center">
                                <KeyRound className="w-5 h-5 mr-2 text-blue-600"/> 
                                Recuperar Acesso
                            </h3>
                            <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="p-6">
                            {resetSuccess ? (
                                <div className="text-center py-4">
                                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                        <ArrowRight className="h-6 w-6 text-green-600" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900">Solicitação Enviada!</h3>
                                    <p className="mt-2 text-sm text-gray-500">{resetSuccess}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleResetPassword} className="space-y-4">
                                    <p className="text-sm text-gray-600 mb-4">
                                        Informe o e-mail associado à sua conta. Se ele existir em nossa base local, entraremos em contato ou resetaremos para uma senha padrão.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                                        <input 
                                            type="email" 
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            placeholder="seu@email.com"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <button 
                                            type="button" 
                                            onClick={() => setShowResetModal(false)}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                                        >
                                            Cancelar
                                        </button>
                                        <button 
                                            type="submit" 
                                            disabled={resetLoading}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex justify-center items-center disabled:opacity-50"
                                        >
                                            {resetLoading ? <Loader2 className="animate-spin w-4 h-4" /> : 'Enviar Solicitação'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginScreen;